import React from 'react';
import { useNavigate } from 'react-router-dom';
import { VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED, BIRTHDAY_REWARDS_UI_ENABLED } from '../../../shared/config/featureFlags';

/**
 * RewardForm - Form for creating a new reward
 * @param {Object} props
 * @param {Object} props.form - Form state object
 * @param {Object} props.errors - Form validation errors
 * @param {boolean} props.formLoading - Whether form is submitting
 * @param {boolean} props.success - Whether form submission was successful
 * @param {Object} props.rewardsettings - Reward settings from Redux
 * @param {Function} props.isEnabled - Function to check if reward type is enabled
 * @param {Function} props.onFormChange - Callback when form field changes
 * @param {Function} props.onSubmit - Callback when form is submitted
 */
export function RewardForm({
    form,
    errors,
    formLoading,
    success,
    rewardsettings,
    isEnabled,
    onFormChange,
    onSubmit,
}) {
    const navigate = useNavigate();
    const TITLE_MAX_LENGTH = 60;
    const DESCRIPTION_MAX_LENGTH = 85;

    const pointsUnavailable = !rewardsettings?.points_enabled;
    const visitsUnavailable =
        !rewardsettings?.visits_enabled || !VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED;
    const punchUnavailable =
        !rewardsettings?.visits_enabled ||
        !rewardsettings?.punch_cards_enabled ||
        !VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED;
    const birthdayUnavailable =
        !BIRTHDAY_REWARDS_UI_ENABLED || !rewardsettings?.birthday_enabled;

    const unavail = (flag) => (flag ? ' (unavailable)' : '');

    return (
        <div className="flex-1 bg-white shadow-md rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-primary mb-2">Create a New Reward</h2>
            <p className="text-gray-600 mb-4">
                Choose a reward type. Types you have not turned on in Rewards Settings appear as{' '}
                <span className="font-medium">(unavailable)</span> and cannot be selected for new rewards.
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
                <label className="block font-medium mb-1">Reward type</label>
                <select
                    className={`w-full border p-3 rounded ${!isEnabled(form.type) ? 'bg-gray-100 text-gray-400' : ''}`}
                    value={form.type}
                    onChange={(e) => onFormChange('type', e.target.value)}
                >
                    <option value="points" disabled={pointsUnavailable}>
                        Points{unavail(pointsUnavailable)}
                    </option>
                    <option value="visits" disabled={visitsUnavailable}>
                        Visits{unavail(visitsUnavailable)}
                    </option>
                    <option value="punch" disabled={punchUnavailable}>
                        Punch Card{unavail(punchUnavailable)}
                    </option>
                    <option value="birthday" disabled={birthdayUnavailable}>
                        Birthday{unavail(birthdayUnavailable)}
                    </option>
                </select>
                {VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED && (
                    <p className="mt-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                        <span className="font-semibold text-gray-900">Punch cards</span> are stamp-style pass art. How
                        punches accrue can follow <span className="font-semibold">visits</span>,{' '}
                        <span className="font-semibold">money spent</span>, or a{' '}
                        <span className="font-semibold">prepaid package</span> (for example, a 10-pack where each
                        redemption uses one punch).
                        <span className="mt-1.5 block">
                            Choose <span className="font-semibold">Punch Card</span> above for visit-based rewards that
                            pair with punch strip artwork, or <span className="font-semibold">Visits</span> for the same
                            mechanics without requiring punch cards in settings. Turn on{' '}
                            <span className="font-semibold">Punch cards</span> in Rewards Settings for strip artwork,
                            then set your strip under <span className="font-semibold">Settings → Apple Pass</span>.
                        </span>
                    </p>
                )}

                <div>
                    <label className="block font-medium mb-1">Reward Title:</label>
                    <input
                        type="text"
                        className="w-full border p-3 rounded"
                        placeholder="Example: Free coffee"
                        value={form.title}
                        onChange={(e) => onFormChange('title', e.target.value)}
                        required
                        maxLength={TITLE_MAX_LENGTH}
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                        {form.title.length}/{TITLE_MAX_LENGTH}
                    </div>
                    {errors.title && <p className="text-sm text-red-500 mt-1">{errors.title}</p>}
                </div>

                <div>
                    <label className="block font-medium mb-1">Reward Description:</label>
                    <textarea
                        className="w-full border p-3 rounded"
                        placeholder="Example: Free coffee for 10 points"
                        value={form.description}
                        onChange={(e) => onFormChange('description', e.target.value)}
                        rows={3}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                        {form.description.length}/{DESCRIPTION_MAX_LENGTH}
                    </div>
                    {errors.description && (
                        <p className="text-sm text-red-500 mt-1">{errors.description}</p>
                    )}
                </div>

                {form.type === 'birthday' && isEnabled('birthday') && (
                    <div>
                        <label className="block font-medium mb-1">Birthday eligibility window</label>
                        <select
                            className="w-full border p-3 rounded"
                            value={form.birthday_window || 'week'}
                            onChange={(e) => onFormChange('birthday_window', e.target.value)}
                        >
                            <option value="day">On their birthday (day)</option>
                            <option value="week">Birthday week (±3 days)</option>
                            <option value="month">Birthday month</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Matches the window used when checking if a customer can redeem this reward.
                        </p>
                    </div>
                )}

                {form.type !== 'birthday' && (
                    <>
                        <label className="block font-medium mb-1">Cost:</label>
                        <input
                            type="number"
                            className="w-full border p-3 rounded"
                            placeholder="Cost"
                            value={form.cost}
                            onChange={(e) => onFormChange('cost', e.target.value)}
                            required
                        />
                        {errors.cost && <p className="text-sm text-red-500 mt-1">{errors.cost}</p>}

                        {/* Max redemption - commented out for now
                        <div className="flex items-center gap-2 mt-2 mb-2">
                            <input
                                type="checkbox"
                                id="hasMaxRedemptions"
                                checked={form.hasMaxRedemptions}
                                onChange={(e) => onFormChange('hasMaxRedemptions', e.target.checked)}
                            />
                            <label htmlFor="hasMaxRedemptions" className="mb-0 text-sm">
                                Limit redemptions
                            </label>
                        </div>

                        <input
                            type="number"
                            className={`w-full border p-3 rounded ${!form.hasMaxRedemptions ? 'bg-gray-100 text-gray-400' : ''}`}
                            placeholder="Max Redemption"
                            value={form.max_redemptions}
                            onChange={(e) => onFormChange('max_redemptions', e.target.value)}
                            required={form.hasMaxRedemptions}
                            disabled={!form.hasMaxRedemptions}
                        />
                        {errors.max_redemptions && (
                            <p className="text-sm text-red-500 mt-1">{errors.max_redemptions}</p>
                        )}
                        */}
                    </>
                )}

                <button
                    type="submit"
                    className={`w-full sm:w-auto bg-primary text-white px-4 py-3 rounded hover:bg-opacity-90 transition disabled:opacity-50 ${!isEnabled(form.type) ? 'bg-gray-400 cursor-not-allowed' : ''}`}
                    disabled={!isEnabled(form.type) || formLoading}
                >
                    {formLoading ? 'Adding...' : 'Add Reward'}
                </button>

                {success && <p className="text-green-600 text-sm">Saved successfully!</p>}
                {errors.general && (
                    <div className="mt-2">
                        <p className="text-red-600 text-sm mb-2">{errors.general}</p>
                        {(errors.general.includes('limit exceeded') || errors.general.includes('Please upgrade')) && (
                            <button
                                type="button"
                                onClick={() => navigate('/dashboard/settings/membership')}
                                className="mt-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition text-sm font-medium"
                            >
                                Upgrade Plan
                            </button>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
}

